import {
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	CircularProgress,
	List,
	ListItem,
	ListItemText,
	Switch,
	Typography,
	useTheme,
} from '@mui/material';
import { post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { enqueueSnackbar } from 'notistack';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../../contexts/UserContext';
import { cardStyle } from '../../../theme';

export function SkillSwap({ agentId, allSkillIds, activeSkillIds, skillNames, skillSwapConfig, onSkillsUpdated }) {
	const theme = useTheme();
	const userContext = useContext(UserContext);

	const { min, max, hideInbound = true, hideOutbound = false } = skillSwapConfig;

	const [activeSkills, setActiveSkills] = useState(new Set());
	const [saving, setSaving] = useState(false);
	const [dirty, setDirty] = useState(false);

	// Initialize from the actual active skills passed in
	useEffect(() => {
		if (activeSkillIds?.length) {
			setActiveSkills(new Set(activeSkillIds));
			setDirty(false);
		}
	}, [activeSkillIds]);

	const activeCount = activeSkills.size;
	const meetsMin = activeCount >= min;
	const meetsMax = activeCount <= max;
	const isValid = meetsMin && meetsMax;

	function handleToggle(skillId) {
		setActiveSkills((prev) => {
			const next = new Set(prev);
			if (next.has(skillId)) {
				next.delete(skillId);
			} else {
				next.add(skillId);
			}
			setDirty(true);
			return next;
		});
	}

	async function handleUpdate() {
		if (!isValid) return;
		setSaving(true);
		const previousActiveSkills = new Set(activeSkills);
		try {
			const session = await fetchAuthSession();
			const response = await post({
				apiName: 'cdyxoutreach',
				path: '/approver/skill-swap',
				options: {
					headers: {
						Authorization: `Bearer ${session.tokens.idToken}`,
						'x-api-key': userContext.apiKey,
					},
					body: {
						agentId,
						activeSkills: Array.from(activeSkills),
						min,
						max,
						hideInbound,
						hideOutbound,
					},
				},
			}).response;
			const data = await response.body.json();

			// Update active skills from the API response
			if (data.skills) {
				const updatedActiveSkillIds = data.skills
					.filter(skill => skill.isActive)
					.map(skill => String(skill.skillId));
				setActiveSkills(new Set(updatedActiveSkillIds));
				if (onSkillsUpdated) {
					onSkillsUpdated(updatedActiveSkillIds);
				}
			}

			enqueueSnackbar('Skills updated successfully', { variant: 'success' });
			setDirty(false);
		} catch (error) {
			console.error('Error updating skills', error);
			enqueueSnackbar('Failed to update skills', { variant: 'error' });
			// Reset to previous state on failure
			setActiveSkills(previousActiveSkills);
			setDirty(false);
		}
		setSaving(false);
	}

	function getValidationMessage() {
		if (!meetsMin) return `Select at least ${min} skill${min > 1 ? 's' : ''}`;
		if (!meetsMax) return `Select at most ${max} skill${max > 1 ? 's' : ''}`;
		return null;
	}

	return (
		<Box
			sx={{ width: '100%', backgroundColor: theme.palette.background.default, minHeight: '100%' }}
			display="flex"
			flexDirection="column"
			alignItems="center"
			p={3}
		>
			<Box sx={{ maxWidth: '600px', width: '100%' }}>
				<Card variant="outlined" sx={cardStyle}>
					<CardHeader
						title="Skill Swap"
						subheader={`Toggle your active skills. Min: ${min} · Max: ${max}`}
						action={
							<Typography
								variant="body2"
								color={isValid ? 'text.secondary' : 'error'}
								sx={{ mt: 1, mr: 1 }}
							>
								{activeCount} active
							</Typography>
						}
					/>
					<CardContent>
						{(!allSkillIds || allSkillIds.length === 0) && (
							<Typography variant="body2" color="text.secondary">
								No skills available for your agent.
							</Typography>
						)}

						{allSkillIds?.length > 0 && (
							<List disablePadding>
								{allSkillIds.map((skillId) => {
									const isActive = activeSkills.has(skillId);

									return (
										<ListItem
											key={skillId}
											divider
											secondaryAction={
												<Switch
													edge="end"
													checked={isActive}
													onChange={() => handleToggle(skillId)}
													color="primary"
												/>
											}
										>
											<ListItemText
												primary={skillNames?.[skillId] || skillId}
												secondary={skillNames?.[skillId] ? skillId : undefined}
												primaryTypographyProps={{
													fontWeight: isActive ? 600 : 400,
													color: isActive ? 'text.primary' : 'text.secondary',
												}}
											/>
										</ListItem>
									);
								})}
							</List>
						)}

						{!isValid && dirty && (
							<Typography variant="body2" color="error" sx={{ mt: 1 }}>
								{getValidationMessage()}
							</Typography>
						)}

						<Box display="flex" justifyContent="flex-end" mt={2}>
							<Button
								variant="contained"
								onClick={handleUpdate}
								disabled={saving || !dirty || !isValid}
							>
								{saving ? <CircularProgress size={20} /> : 'Update Skills'}
							</Button>
						</Box>
					</CardContent>
				</Card>
			</Box>
		</Box>
	);
}
